import { MeasurementSystem } from '@warebee/shared/data-access-layout-manager';
import { TFunction } from 'i18next';

export type MeasureEditor = 'input' | 'drop-down';
export type MeasureType =
  | 'size'
  | 'volume'
  | 'weight'
  | 'local-date-time'
  | 'distance';

export function getMeasurementSystemTitle(
  system: MeasurementSystem,
  t: TFunction<'measures'>,
) {
  switch (system) {
    case MeasurementSystem.METRIC:
      return t('Metric', { ns: 'measures' });
    case MeasurementSystem.IMPERIAL:
      return t('Imperial', { ns: 'measures' });
  }
}

export function getDefaultMeasure(
  system: MeasurementSystem,
  type: MeasureType,
) {
  switch (type) {
    case 'size':
      switch (system) {
        case MeasurementSystem.METRIC:
          return 'cm';
        case MeasurementSystem.IMPERIAL:
          return 'in';
      }
      break;
    case 'volume':
      switch (system) {
        case MeasurementSystem.METRIC:
          return 'cm3';
        case MeasurementSystem.IMPERIAL:
          return 'in3';
      }
      break;
    case 'weight':
      switch (system) {
        case MeasurementSystem.METRIC:
          return 'kg';
        case MeasurementSystem.IMPERIAL:
          return 'lb';
      }
      break;
    case 'distance':
      switch (system) {
        case MeasurementSystem.METRIC:
          return 'm';
        case MeasurementSystem.IMPERIAL:
          return 'ft';
      }
      break;
  }
}

export function getMeasureTitle(key: string, t: TFunction<'measures'>) {
  switch (key) {
    // Length/Distance
    case 'mm':
      return t('mm', { ns: 'measures' });
    case 'cm':
      return t('cm', { ns: 'measures' });
    case 'm':
      return t('m', { ns: 'measures' });
    case 'km':
      return t('km', { ns: 'measures' });
    case 'in':
      return t('in', { ns: 'measures' });
    case 'ft':
      return t('ft', { ns: 'measures' });
    case 'yd':
      return t('yd', { ns: 'measures' });
    case 'mi':
      return t('mi', { ns: 'measures' });

    // Volume
    case 'cm3':
      return t('cm³', { ns: 'measures' });
    case 'm3':
      return t('m³', { ns: 'measures' });
    case 'in3':
      return t('in³', { ns: 'measures' });
    case 'ft3':
      return t('ft³', { ns: 'measures' });
    case 'l':
      return t('L', { ns: 'measures' });
    case 'ml':
      return t('mL', { ns: 'measures' });
    case 'gal':
      return t('gal', { ns: 'measures' });
    case 'qt':
      return t('qt', { ns: 'measures' });
    case 'pt':
      return t('pt', { ns: 'measures' });

    // Weight/Mass
    case 'kg':
      return t('kg', { ns: 'measures' });
    case 'g':
      return t('g', { ns: 'measures' });
    case 'mg':
      return t('mg', { ns: 'measures' });
    case 'lb':
      return t('lb', { ns: 'measures' });
    case 'oz':
      return t('oz', { ns: 'measures' });

    // Time
    case 's':
      return t('s', { ns: 'measures' });
    case 'min':
      return t('min', { ns: 'measures' });
    case 'h':
      return t('h', { ns: 'measures' });
    case 'day':
      return t('day', { ns: 'measures' });

    // Area
    case 'm2':
      return t('m²', { ns: 'measures' });
    case 'cm2':
      return t('cm²', { ns: 'measures' });
    case 'ft2':
      return t('ft²', { ns: 'measures' });
    case 'in2':
      return t('in²', { ns: 'measures' });

    // Speed
    case 'km/h':
      return t('km/h', { ns: 'measures' });
    case 'm/s':
      return t('m/s', { ns: 'measures' });
    case 'mph':
      return t('mph', { ns: 'measures' });

    // Temperature
    case '°C':
      return t('°C', { ns: 'measures' });
    case '°F':
      return t('°F', { ns: 'measures' });

    // Pressure
    case 'Pa':
      return t('Pa', { ns: 'measures' });
    case 'kPa':
      return t('kPa', { ns: 'measures' });
    case 'bar':
      return t('bar', { ns: 'measures' });
    case 'psi':
      return t('psi', { ns: 'measures' });

    // Energy
    case 'J':
      return t('J', { ns: 'measures' });
    case 'kJ':
      return t('kJ', { ns: 'measures' });
    case 'cal':
      return t('cal', { ns: 'measures' });
    case 'kcal':
      return t('kcal', { ns: 'measures' });

    // Power
    case 'W':
      return t('W', { ns: 'measures' });
    case 'kW':
      return t('kW', { ns: 'measures' });
    case 'hp':
      return t('hp', { ns: 'measures' });

    // Currency
    case 'USD':
      return t('USD', { ns: 'measures' });
    case 'EUR':
      return t('EUR', { ns: 'measures' });
    case 'GBP':
      return t('GBP', { ns: 'measures' });

    default:
      return key;
  }
}

export function getMeasureEditor(mappingMeasure: MeasureType): MeasureEditor {
  switch (mappingMeasure) {
    case 'local-date-time':
      return 'input';
    default:
      return 'drop-down';
  }
}
